/*
  Deals by category (sections/sale-category-browser.liquid)

  The page arrives with the first category's first page of deals, 72 to a page. This
  makes the rail into tabs and loads everything else in place, a page at a time, from
  sections/sale-deals-grid.liquid rendered on the collection's own URL
  (/collections/<handle>?section_id=sale-deals-grid&page=2&sort_by=…), where Shopify
  sorts, filters and pages the products:
  - a rail link shows its category instead of leaving for the collection page, and puts
    #deals-<collection handle> in the address so the view can be shared; opening the
    page with that hash, or following such a link on the page, shows the category and
    scrolls the section into view;
  - the page numbers, sort and brand menu load the matching page of the category, so the
    sort and brand filter cover the whole category, not just the page shown; a brand is
    kept in the address too (#deals-<handle>?brand=<brand>);
  - previous / next step through the categories, skipping empty ones, and wrap round;
  - cards elsewhere on the page with "Open cards in Deals by category" (Sale tiles) open
    here instead of leaving: a brand opens the All deals tab filtered to it, any other
    card shows its collection, with no rail tab chosen.
  An empty category, or a brand with nothing in one, shows a message and a button back
  to the first tab. If a page can't be loaded, the browser goes to the collection page.

  A custom element, so it sets itself up again whenever the theme editor re-renders
  the section, and tidies up when removed. The end of the file keeps the theme's bulk
  quick add working on the cards loaded here.
*/
if (!customElements.get('sale-category-browser')) {
  customElements.define(
    'sale-category-browser',
    class SaleCategoryBrowser extends HTMLElement {
      connectedCallback() {
        this.tabs = Array.from(this.querySelectorAll('[data-sale-tab]'));
        this.panels = this.querySelector('[data-sale-panels]');
        if (!this.tabs.length || !this.panels) return;

        this.heading = this.querySelector('[data-sale-heading]');
        this.status = this.querySelector('[data-sale-status]');
        this.sort = this.querySelector('[data-sale-sort]');
        this.brand = this.querySelector('[data-sale-brand]');
        this.pager = this.querySelector('[data-sale-pager]');
        this.emptyTemplate = this.querySelector('template[data-sale-empty]');
        this.feedSection = this.dataset.feedSection || 'sale-deals-grid';
        this.cache = new Map();
        this.ticket = 0;
        this.brandValue = '';

        this.controller = new AbortController();
        const options = { signal: this.controller.signal };
        this.addEventListener('click', (event) => this.onClick(event), options);
        document.addEventListener('click', (event) => this.onCardClick(event), options);
        if (this.sort) {
          // Hidden in the markup: without this script the menu would do nothing.
          this.sort.closest('[data-sale-sort-wrap]').hidden = false;
          this.sort.addEventListener('change', () => this.open(this.target, { keepBrand: true }), options);
        }
        if (this.brand) {
          this.brand.addEventListener(
            'change',
            () => {
              this.brandValue = this.brand.value;
              this.open(this.target, { keepBrand: true });
            },
            options
          );
        }
        window.addEventListener('hashchange', () => this.openFromHash(), options);

        // The cards show what is in the cart, so pages fetched before a cart change are stale.
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.cache.clear());
        }

        if (this.pager) this.pager.hidden = this.tabs.filter((tab) => !tab.hasAttribute('data-empty')).length < 2;
        const active = this.tabs.findIndex((tab) => tab.getAttribute('aria-current') === 'true');
        this.index = Math.max(0, active);
        this.target = this.tabTarget(this.index);

        // What came with the page: the first tab's first page, in the collection's own order.
        this.panel = this.panels.querySelector('[data-sale-panel]');
        this.dialogs = this.dialogsOf(this.panel);
        this.view = this.viewUrl(this.target, 1);
        const brands = this.panel && this.panel.querySelector('template[data-sale-brands]');
        if (brands) this.fillBrands(brands);

        if (!this.openFromHash()) this.updatePager();
        // Outside a collection page Shopify leaves filters empty, so the brands of the
        // page that came with the page come from a quiet fetch of it on its collection URL.
        if (this.brand && !brands && this.view === this.viewUrl(this.target, 1)) this.loadBrands();
      }

      disconnectedCallback() {
        if (this.controller) this.controller.abort();
        if (this.loading) this.loading.abort();
        if (this.unsubscribe) this.unsubscribe();
      }

      // What the panel can show: a tab of the rail, or a collection opened by a card.
      tabTarget(index) {
        const tab = this.tabs[index];
        return {
          tab,
          index,
          label: tab.dataset.label || '',
          hash: tab.dataset.hash,
          path: pathOf(tab.getAttribute('href')),
          empty: tab.hasAttribute('data-empty'),
        };
      }

      collectionTarget(handle, label, href) {
        const index = this.tabs.findIndex((tab) => tab.dataset.hash === `deals-${handle}`);
        if (index >= 0) return this.tabTarget(index);
        return {
          tab: null,
          index: -1,
          label: label || '',
          hash: `deals-${handle}`,
          path: pathOf(href || `/collections/${handle}`),
          empty: false,
        };
      }

      show(index, options) {
        return this.open(this.tabTarget(index), options);
      }

      onClick(event) {
        const modified = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0;

        const tab = event.target.closest('[data-sale-tab]');
        if (tab && this.contains(tab)) {
          if (modified) return;
          event.preventDefault();
          this.show(this.tabs.indexOf(tab));
          return;
        }

        const page = event.target.closest('[data-sale-page]');
        if (page && this.contains(page)) {
          if (modified) return;
          event.preventDefault();
          const number = Number(page.dataset.salePage) || 1;
          this.open(this.target, { page: number, keepBrand: true, focus: true, scroll: true });
          return;
        }

        const step = event.target.closest('[data-sale-step]');
        if (step) {
          this.show(this.neighbour(Number(step.dataset.saleStep)), { focus: true, scroll: true });
          return;
        }

        if (event.target.closest('[data-sale-show-first]')) {
          this.show(0, { focus: true });
        }
      }

      // A card elsewhere on the page (sale-tile-grid.liquid) opens here instead of leaving,
      // unless this section is hidden (outside its "Show" window): then its link stands.
      onCardClick(event) {
        const card = event.target.closest('a[data-sale-deals-brand], a[data-sale-deals-collection]');
        if (!card || this.contains(card) || event.defaultPrevented) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) return;
        if (this.hidden || !this.getClientRects().length) return;
        event.preventDefault();

        const { saleDealsBrand, saleDealsCollection, saleDealsLabel } = card.dataset;
        const collection = saleDealsCollection
          ? this.collectionTarget(saleDealsCollection, saleDealsLabel, card.getAttribute('href'))
          : null;
        if (saleDealsBrand) {
          // The card's collection stands in if the brand turns out to have nothing in All
          // deals (Shopify can list a brand whose products the storefront doesn't show).
          const all = this.tabs.findIndex((tab) => tab.dataset.saleTab === 'all');
          this.brandValue = saleDealsBrand;
          this.open(this.tabTarget(Math.max(0, all)), { keepBrand: true, fallback: collection });
        } else {
          this.open(collection);
        }
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      // #deals-<handle>, optionally ?brand=<brand>, opens that category (or a collection a
      // card on the page opens) and brings the section into view: no element carries that
      // id, so the browser would not scroll by itself.
      openFromHash() {
        const [base, query] = window.location.hash.replace('#', '').split('?');
        if (!base || !base.startsWith('deals-')) return false;
        let target = null;
        const index = this.tabs.findIndex((tab) => tab.dataset.hash === base);
        if (index >= 0) {
          target = this.tabTarget(index);
        } else {
          const handle = base.slice('deals-'.length);
          const card = document.querySelector(`a[data-sale-deals-collection="${CSS.escape(handle)}"]`);
          if (card) target = this.collectionTarget(handle, card.dataset.saleDealsLabel, card.getAttribute('href'));
        }
        if (!target) return false;

        const brand = query ? new URLSearchParams(query).get('brand') : null;
        this.brandValue = brand || '';
        this.open(target, { keepBrand: true, updateHash: false });
        window.requestAnimationFrame(() => this.scrollIntoView({ behavior: 'smooth', block: 'start' }));
        return true;
      }

      async open(
        target,
        { page = 1, keepBrand = false, focus = false, scroll = false, updateHash = true, fallback = null } = {}
      ) {
        if (!target) return;
        const ticket = ++this.ticket;
        this.target = target;
        if (target.index >= 0) this.index = target.index;
        // A new category starts on all its brands.
        if (!keepBrand) this.brandValue = '';

        this.tabs.forEach((tab) => tab.setAttribute('aria-current', tab === target.tab ? 'true' : 'false'));
        if (this.heading) this.heading.textContent = target.label;
        if (updateHash && window.history.replaceState) {
          const brand = this.brandValue ? `?brand=${encodeURIComponent(this.brandValue)}` : '';
          window.history.replaceState(null, '', `#${target.hash}${brand}`);
        }
        this.updatePager();
        if (target.tab) this.centreTab(target.tab);

        if (target.empty) {
          if (this.loading) this.loading.abort();
          this.setPanel(this.emptyPanel(target.label, target.index === 0), null);
          this.fillBrands(null);
        } else {
          const url = this.viewUrl(target, page);
          if (url !== this.view) {
            let panel = await this.fetchPanel(url);
            // Something else was chosen meanwhile, or the browser is off to the collection.
            if (!panel || ticket !== this.ticket) return;
            if (this.brandValue && panel.dataset.count === '0' && fallback) {
              this.brandValue = '';
              this.open(fallback, { focus, scroll, updateHash });
              return;
            }
            if (this.brandValue && panel.dataset.count === '0') {
              // A brand with nothing here: say so, and keep the menu to pick another.
              const brands = panel.querySelector('template[data-sale-brands]');
              panel = this.emptyPanel(this.brandValue, false);
              if (brands) panel.prepend(brands);
            }
            this.setPanel(panel, url);
          }
          this.announce(target.label);
        }

        if (scroll) this.scrollToTop();
        if (focus && this.heading) {
          this.heading.setAttribute('tabindex', '-1');
          this.heading.focus({ preventScroll: true });
        }
      }

      // The collection's URL with the page, sort and brand: what the section is rendered
      // on, and what bulk quick add re-renders cards on.
      viewUrl(target, page) {
        const params = new URLSearchParams();
        if (page > 1) params.set('page', page);
        if (this.sort && this.sort.value) params.set('sort_by', this.sort.value);
        if (this.brandValue) params.set(this.brandParam || 'filter.p.vendor', this.brandValue);
        const query = params.toString();
        return query ? `${target.path}?${query}` : target.path;
      }

      async fetchPanel(url) {
        if (this.loading) this.loading.abort();
        const loading = new AbortController();
        this.loading = loading;
        this.panels.setAttribute('aria-busy', 'true');
        try {
          let html = this.cache.get(url);
          if (!html) {
            const response = await fetch(withSection(url, this.feedSection), { signal: loading.signal });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            html = await response.text();
            this.cache.set(url, html);
          }
          const panel = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-sale-panel]');
          if (!panel) throw new Error('No deals in the response');
          return panel;
        } catch (error) {
          if (error.name === 'AbortError') return null;
          window.location.assign(url);
          return null;
        } finally {
          if (this.loading === loading) {
            this.loading = null;
            this.panels.removeAttribute('aria-busy');
          }
        }
      }

      setPanel(panel, url) {
        // The theme moves each card's quick add dialog to the end of <body>, out of the
        // panel: take the old page's with it, or they pile up and shadow the new ones
        // (the dialogs are found by id).
        this.dialogs.forEach((dialog) => dialog.remove());
        this.dialogs = Array.from(panel.querySelectorAll('modal-dialog'));
        if (url) {
          panel.dataset.saleFeedUrl = url;
          this.dialogs.forEach((dialog) => {
            dialog.dataset.saleFeedUrl = url;
          });
        }

        const grid = panel.querySelector('.sale-browser__grid');
        if (grid) {
          grid.className = grid.className
            .replace(/grid--\d-col-desktop/, `grid--${this.dataset.columns || 4}-col-desktop`)
            .replace(/grid--\d-col-tablet-down/, `grid--${this.dataset.columnsMobile || 2}-col-tablet-down`);
        }
        const brands = panel.querySelector('template[data-sale-brands]');
        if (brands || url) this.fillBrands(brands);

        this.panels.replaceChildren(panel);
        this.panel = panel;
        this.view = url;
      }

      // The dialogs of a panel already in the page have moved to <body>; find them by
      // their openers.
      dialogsOf(panel) {
        if (!panel) return [];
        return Array.from(panel.querySelectorAll('modal-opener[data-modal]'))
          .map((opener) => document.querySelector(opener.dataset.modal))
          .filter((dialog) => dialog && !panel.contains(dialog))
          .concat(Array.from(panel.querySelectorAll('modal-dialog')));
      }

      emptyPanel(label, first) {
        const panel = document.createElement('div');
        panel.className = 'sale-browser__panel';
        panel.setAttribute('data-sale-panel', '');
        if (this.emptyTemplate) panel.append(this.emptyTemplate.content.cloneNode(true));
        panel.querySelectorAll('[data-sale-fill]').forEach((node) => {
          node.textContent = node.textContent.replace('[category]', label);
        });
        // No button back to the first tab on the first tab itself.
        if (first) {
          const back = panel.querySelector('[data-sale-show-first]');
          if (back) back.remove();
        }
        return panel;
      }

      // The Brand menu lists the category's brands, and shows when there are two or more,
      // or one is chosen. A brand asked for by a card or the address stays chosen even if
      // the category has nothing from it.
      fillBrands(template) {
        if (!this.brand) return;
        const options = template
          ? Array.from(template.content.querySelectorAll('option')).map((option) => document.importNode(option, true))
          : [];
        if (template && template.dataset.param) this.brandParam = template.dataset.param;
        if (this.brandValue && !options.some((option) => option.value === this.brandValue)) {
          options.unshift(new Option(this.brandValue, this.brandValue));
        }
        const all = this.brand.querySelector('option[value=""]');
        this.brand.replaceChildren(all, ...options);
        this.brand.value = this.brandValue;
        this.brand.closest('[data-sale-brand-wrap]').hidden = options.length < 2 && !this.brandValue;
      }

      loadBrands() {
        const target = this.target;
        if (!target || target.empty) return;
        const url = this.viewUrl(target, 1);
        fetch(withSection(url, this.feedSection))
          .then((response) => (response.ok ? response.text() : Promise.reject(new Error(`HTTP ${response.status}`))))
          .then((html) => {
            this.cache.set(url, html);
            const template = new DOMParser().parseFromString(html, 'text/html').querySelector('template[data-sale-brands]');
            if (template && this.target === target && !this.brandValue) this.fillBrands(template);
          })
          .catch(() => {});
      }

      announce(label) {
        if (!this.status || !this.panel) return;
        const page = Number(this.panel.dataset.page) || 1;
        const pages = Number(this.panel.dataset.pages) || 1;
        const brand = this.brandValue ? `, ${this.brandValue}` : '';
        this.status.textContent = pages > 1 ? `${label}${brand}, page ${page} of ${pages}` : `${label}${brand}`;
      }

      scrollToTop() {
        const top = this.querySelector('.sale-browser__main') || this;
        if (top.getBoundingClientRect().top < 0) top.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      // Bring the chosen tab into view on the sideways-scrolling rail of small screens,
      // without scrolling the page (scrollIntoView would).
      centreTab(tab) {
        const rail = tab.closest('.sale-browser__rail');
        if (rail && rail.scrollWidth > rail.clientWidth) {
          rail.scrollTo({ left: tab.offsetLeft - (rail.clientWidth - tab.offsetWidth) / 2, behavior: 'smooth' });
        }
      }

      neighbour(direction) {
        const total = this.tabs.length;
        for (let step = 1; step <= total; step += 1) {
          const index = (this.index + direction * step + total * step) % total;
          if (!this.tabs[index].hasAttribute('data-empty')) return index;
        }
        return this.index;
      }

      updatePager() {
        if (!this.pager || this.pager.hidden) return;
        const [previous, next] = this.pager.querySelectorAll('[data-sale-step-name]');
        if (previous) previous.textContent = this.tabs[this.neighbour(-1)].dataset.label || '';
        if (next) next.textContent = this.tabs[this.neighbour(1)].dataset.label || '';
      }
    }
  );
}

function pathOf(href) {
  return new URL(href, window.location.origin).pathname;
}

function withSection(url, sectionId) {
  return `${url}${url.includes('?') ? '&' : '?'}section_id=${encodeURIComponent(sectionId)}`;
}

/*
  Bulk quick add on the deals cards. quick-add-bulk.js and quick-order-list.js refresh a
  card by rendering its section (the data-id of the card's .collection) again on the
  current page's URL, and finding the card in it. A page of deals loaded above comes
  from sale-deals-grid on the category's URL instead, which the panel (and each moved
  dialog) keeps in data-sale-feed-url, so:
  - both elements render on that URL;
  - quick-add-bulk's first add sends no URL at all, and Shopify would render on this
    page, where sale-deals-grid has no collection: that one request gets the URL;
  - Shopify renders a cart request's sections on the URL's path and page but ignores
    its sort and brand filter, so for a sorted or filtered page the section in the
    response is swapped for a render of the full URL;
  - after a cart change made elsewhere, every card fetches the section for itself (72
    fetches for a page of cards); here they share one fetch per URL.
  Cards that came with the page keep the theme's URLs, apart from the shared fetch.
*/
(() => {
  if (window.saleDealsQuickAdd) return;
  window.saleDealsQuickAdd = true;

  const feedUrlOf = (element) => {
    const holder = element.closest('[data-sale-feed-url]');
    return holder ? holder.dataset.saleFeedUrl : null;
  };
  const isDeals = (element) => Boolean(element.closest('sale-category-browser, [data-sale-feed-url]'));

  const shared = new Map();
  const sectionDocument = (url) => {
    if (!shared.has(url)) {
      const request = fetch(url)
        .then((response) => response.text())
        .then((text) => new DOMParser().parseFromString(text, 'text/html'));
      shared.set(url, request);
      // Shared by the refreshes that follow one cart change, then let go.
      const forget = () => window.setTimeout(() => shared.delete(url), 1500);
      request.then(forget, forget);
    }
    return shared.get(url);
  };

  // Cart requests that render sale-deals-grid: add the URL to quick-add-bulk's first add,
  // and as Shopify renders a cart request's sections on the URL's path and page only
  // (not its sort or brand filter), swap in a render of the full URL when it has them,
  // or the card would not be found in it.
  let sectionsUrl = null;
  const nativeFetch = window.fetch;
  window.fetch = function (resource, options) {
    let body = null;
    if (options && typeof options.body === 'string' && options.body.includes('"sale-deals-grid"')) {
      try {
        body = JSON.parse(options.body);
      } catch (error) {
        body = null;
      }
    }
    if (!body || !Array.isArray(body.sections) || !body.sections.includes('sale-deals-grid')) {
      return nativeFetch.call(window, resource, options);
    }

    if (!body.sections_url && sectionsUrl) {
      body.sections_url = sectionsUrl;
      options = Object.assign({}, options, { body: JSON.stringify(body) });
    }
    const url = body.sections_url;
    const request = nativeFetch.call(window, resource, options);
    if (!url || !/[?&](sort_by|filter\.)/.test(url)) return request;

    return request.then(async (response) => {
      if (!response.ok) return response;
      try {
        const state = await response.clone().json();
        if (!state.sections || !('sale-deals-grid' in state.sections)) return response;
        const fresh = await nativeFetch.call(window, withSection(url, 'sale-deals-grid'));
        if (!fresh.ok) return response;
        state.sections['sale-deals-grid'] = await fresh.text();
        return new Response(JSON.stringify(state), {
          status: response.status,
          statusText: response.statusText,
          headers: response.headers,
        });
      } catch (error) {
        return response;
      }
    });
  };

  customElements.whenDefined('quick-add-bulk').then(() => {
    const proto = customElements.get('quick-add-bulk').prototype;

    const getSectionsUrl = proto.getSectionsUrl;
    proto.getSectionsUrl = function () {
      return feedUrlOf(this) || getSectionsUrl.call(this);
    };

    const addToCart = proto.addToCart;
    proto.addToCart = function (event) {
      sectionsUrl = feedUrlOf(this);
      try {
        return addToCart.call(this, event);
      } finally {
        sectionsUrl = null;
      }
    };

    const onCartUpdate = proto.onCartUpdate;
    proto.onCartUpdate = function () {
      if (!isDeals(this)) return onCartUpdate.call(this);
      const sectionId = this.closest('.collection').dataset.id;
      return sectionDocument(withSection(this.getSectionsUrl(), sectionId))
        .then((html) => {
          const source = html.querySelector(`#quick-add-bulk-${this.dataset.id}-${sectionId}`);
          if (source) this.innerHTML = source.innerHTML;
        })
        .catch((error) => console.error(error));
    };
  });

  customElements.whenDefined('quick-order-list').then(() => {
    const proto = customElements.get('quick-order-list').prototype;

    const getSectionsUrl = proto.getSectionsUrl;
    proto.getSectionsUrl = function () {
      return feedUrlOf(this) || getSectionsUrl.call(this);
    };

    const refresh = proto.refresh;
    proto.refresh = function () {
      if (!isDeals(this)) return refresh.call(this);
      return sectionDocument(withSection(this.getSectionsUrl(), this.dataset.id))
        .then((html) => {
          const source = html.querySelector(`#${this.quickOrderListId}`);
          if (source) this.innerHTML = source.innerHTML;
        })
        .catch((error) => console.error(error));
    };
  });
})();
